/**
 * School-link API flags and LP/OLS matrix helpers for CMS displayConditions and K12 School CTAs.
 */

import { normalizeCanvasLink } from '@/utils/linksUtils';

export type SchoolLinkKind = 'none' | 'launchpad' | 'olsPortal' | 'unknown';

/** Parsed v2 school-link API payload used across LinksContext and display runtime. */
export interface SchoolLinkSnapshot {
  schoolLinkUrl: string | null;
  courseAssigned: boolean;
  /** API `canvasLink` boolean — school link targets Canvas/OLS portal vs Launch Pad. */
  isCanvasPortal: boolean;
}

export const EMPTY_SCHOOL_LINK_SNAPSHOT: SchoolLinkSnapshot = {
  schoolLinkUrl: null,
  courseAssigned: false,
  isCanvasPortal: false,
};

const QUOTE_TRIM = /^"|"$/g;

function normalizeSchoolLink(raw: string | null | undefined): string {
  return (raw ?? '').trim().replace(QUOTE_TRIM, '');
}

/** Optional extra hostname substrings (comma-separated), e.g. `qa-learn,myhost` */
function extraOlsPortalHostMarkers(): string[] {
  const raw = import.meta.env.VITE_SCHOOL_LINK_OLS_HOST_MARKERS as string | undefined;
  if (!raw?.trim()) return [];
  return raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * `none` — empty, sentinel, or unusable.
 * `launchpad` — Launch Pad entry host (school link points at LP).
 * `olsPortal` — K12 Learn / Canvas LMS style hosts used when LP is off and OLS is on.
 * `unknown` — non-empty but not classified.
 */
export function classifySchoolLinkKind(raw: string | null | undefined): SchoolLinkKind {
  const cleaned = normalizeSchoolLink(raw);
  if (!cleaned || cleaned === 'NO_OLS_LINK') return 'none';

  let host: string;
  try {
    const href = cleaned.includes('://') ? cleaned : `https://${cleaned}`;
    host = new URL(href).hostname.toLowerCase();
  } catch {
    return 'unknown';
  }

  if (host.includes('launchpad')) return 'launchpad';

  if (
    host.includes('learn') ||
    host.includes('instructure') ||
    host.includes('canvas') ||
    host.startsWith('ols.') ||
    host.includes('.ols.') ||
    host.startsWith('canvas.') ||
    host.includes('.canvas.')
  ) {
    return 'olsPortal';
  }

  for (const marker of extraOlsPortalHostMarkers()) {
    if (marker && host.includes(marker)) return 'olsPortal';
  }

  return 'unknown';
}

/** Legacy fallback when API omits the `canvasLink` boolean. */
export function inferIsCanvasPortal(
  schoolLinkUrl: string | null | undefined,
  canvasLinkBoolean: boolean | undefined
): boolean {
  if (typeof canvasLinkBoolean === 'boolean') return canvasLinkBoolean;
  return classifySchoolLinkKind(schoolLinkUrl) === 'olsPortal';
}

export interface SchoolLinkApiResponse {
  schoolLink?: string | null;
  canvasURL?: string | null;
  courseAssigned?: boolean;
  canvasLink?: boolean;
}

/** Normalizes v2 school-link JSON (and legacy canvasURL-only responses). */
export function parseSchoolLinkResponse(data: SchoolLinkApiResponse): SchoolLinkSnapshot {
  const raw = data.schoolLink ?? data.canvasURL ?? null;
  const schoolLinkUrl = normalizeCanvasLink(raw);
  return {
    schoolLinkUrl,
    courseAssigned: data.courseAssigned === true,
    isCanvasPortal: inferIsCanvasPortal(schoolLinkUrl, data.canvasLink),
  };
}

/**
 * Parses v1 `/quicklinks/canvas` responses (plain URL text or legacy JSON with `canvasURL`).
 * Milestone landing treats a usable URL as OLS-ready so the portal CTA can render.
 */
export function parseCanvasQuicklinkBody(raw: string): SchoolLinkSnapshot {
  const trimmed = raw.trim();
  if (!trimmed) return EMPTY_SCHOOL_LINK_SNAPSHOT;

  if (trimmed.startsWith('{')) {
    try {
      const data = JSON.parse(trimmed) as SchoolLinkApiResponse;
      const parsed = parseSchoolLinkResponse(data);
      if (parsed.schoolLinkUrl && !parsed.courseAssigned) {
        return { ...parsed, courseAssigned: true, isCanvasPortal: true };
      }
      return parsed;
    } catch {
      /* fall through — body may be a quoted URL string */
    }
  }

  const schoolLinkUrl = normalizeCanvasLink(trimmed);
  return {
    schoolLinkUrl,
    courseAssigned: Boolean(schoolLinkUrl),
    isCanvasPortal: Boolean(schoolLinkUrl),
  };
}

/** True when the current route is the Canvas milestone landing page. */
export function isMilestone1Route(pathname: string): boolean {
  return pathname === '/milestone-1' || pathname.endsWith('/milestone-1');
}

/** OLS state for CMS `displayConditions.olsAvailable` — driven by API `courseAssigned`. */
export function deriveOlsAvailability(courseAssigned: boolean): boolean {
  return courseAssigned;
}

function launchpadFallbackHref(): string | null {
  const v = (import.meta.env.VITE_LAUNCHPAD_URL as string | undefined)?.trim();
  return v || null;
}

/**
 * Resolves the URL opened by the primary K12 School (OLS) CTA.
 * LP on → Launch Pad; LP off + OLS on → Canvas/OLS portal (`schoolLinkUrl`).
 */
export function resolveK12SchoolOpenHref(
  lpOn: boolean,
  olsOn: boolean,
  schoolLinkUrl: string | null | undefined,
  isCanvasPortal: boolean
): string | null {
  if (!lpOn && !olsOn) return null;

  const cleaned = normalizeSchoolLink(schoolLinkUrl);

  if (lpOn) {
    if (isCanvasPortal) return launchpadFallbackHref() || cleaned || null;
    return cleaned || launchpadFallbackHref() || null;
  }

  if (olsOn) return cleaned || null;
  return null;
}

/** K12 School primary CTA: visible unless both LP and OLS are off (matrix). */
export function isK12SchoolCtaVisible(lpOn: boolean, olsOn: boolean): boolean {
  return lpOn || olsOn;
}

/** Calendar / canvas-link inline URLs: LP off + OLS on + usable school link. */
export function showCanvasInlineLinks(
  lpOn: boolean,
  olsOn: boolean,
  schoolLinkUrl: string | null | undefined
): boolean {
  return !lpOn && olsOn && Boolean(normalizeSchoolLink(schoolLinkUrl));
}
